import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import { roleSchema, roleAtLeast } from '@lms/shared-types'
import { getMe } from '../../api/me'
import type { Role } from '@lms/shared-types'
import { useSession, startLogout } from '../../auth/auth-context'
import { flags } from '../../lib/feature-flags'
import { useViewport } from '../../lib/use-viewport'
import { useSidebarCollapse } from '../../lib/use-sidebar-collapse'
import { FadeIn } from '../primitives/FadeIn'
import { ToastProvider } from '../primitives/Toast'
import { Icon } from '../primitives/Icon'
import { ShortcutsHelpModal } from '../shared/ShortcutsHelpModal'
import { BootSplash } from './BootSplash'
import { Sidebar, visibleItems, navIconColor } from './Sidebar'
import { resolveNavIdentity } from './nav-config'
import { TopBar, HEADER_HEIGHT } from './TopBar'
import { ViewingAsBar } from './ViewingAsBar'
import { MobileDrawer } from './MobileDrawer'
import { OfflineBanner } from './OfflineBanner'
import { PolicyAssistantWidget } from '../assistant/PolicyAssistantWidget'
import {
  EffectiveEmployeeProvider,
  useEffectiveRole,
  useEffectiveEmployeeState,
} from './effective-employee'
import { MomentProvider } from '../../lib/moment-context'
import { useGlobalKeymap } from '../../lib/use-global-keymap'
import { useNavCounts } from '../../lib/use-nav-counts'

/**
 * Application shell — wireframe-faithful chrome:
 *
 *   ┌─────────────────────────────────────────────────────┐
 *   │ [≡] Brand   TopBar (viewing-as · identity)          │  full-width
 *   ├──────────┬──────────────────────────────────────────┤
 *   │ Sidebar  │ <main> page content                      │
 *   │  sticky  │                                          │
 *   │ below bar│                                          │
 *   └──────────┴──────────────────────────────────────────┘
 *
 * The TopBar spans the FULL width (brand + collapse toggle on the left); the
 * Sidebar starts *below* it (sticky at top: HEADER_HEIGHT, height fills the
 * rest of the viewport). This makes the brand gradient one clean masthead
 * instead of a band notched around the sidebar.
 *
 * The "Viewing As" pill lives inline in the TopBar (left of the user identity
 * block) for HR / Director / Exec Director users, gated by the `viewingAs`
 * feature flag. RBAC stays enforced inside <ViewingAsBar /> itself.
 *
 * Viewport behaviour:
 *   - desktop ≥1100px : sidebar 190px, sticky under TopBar, user can collapse
 *   - tablet 720-1099 : sidebar auto-collapsed to 56px icon-rail
 *   - mobile <720px   : sidebar hidden; TopBar shows hamburger that opens
 *                       a slide-out drawer with the full nav + sign-out;
 *                       the Viewing As pill is hidden on mobile
 *
 * The shell holds the EffectiveEmployeeProvider so the sidebar, the viewing-as
 * pill and any consumer page share one source of truth for who's being viewed.
 */

interface AppShellProps {
  children: ReactNode
}

export function AppShell({ children }: AppShellProps): JSX.Element {
  return (
    <ToastProvider>
      <OfflineBanner />
      <EffectiveEmployeeProvider>
        <MomentProvider>
          <AppShellInner>{children}</AppShellInner>
        </MomentProvider>
      </EffectiveEmployeeProvider>
    </ToastProvider>
  )
}

/**
 * Mobile bottom navigation bar — visible only on mobile (<720px).
 *
 * Shows the top 4 nav items for the current role as icon + label tabs, plus
 * a "More" button that opens the full-nav drawer. This follows the handover
 * §05 spec: "top + bottom-nav (mobile)".
 *
 * The drawer already handles the full nav list; bottom-nav is the quick-tap
 * primary path for the most common 4 destinations.
 */
interface MobileBottomNavProps {
  currentRole: Role
  currentRoles: Role[]
  onOpenDrawer: () => void
}

const BOTTOM_NAV_MAX = 4

function MobileBottomNav({ currentRole, currentRoles, onOpenDrawer }: Readonly<MobileBottomNavProps>): JSX.Element {
  const location = useLocation()
  const roles = currentRoles.length > 0 ? currentRoles : [currentRole]
  // Reuse the Sidebar's visibility predicate so mobile + desktop never
  // diverge; the bottom-nav just takes the first N visible items.
  const items = visibleItems(currentRole, roles).slice(0, BOTTOM_NAV_MAX)

  return (
    <nav
      aria-label="Primary navigation"
      data-testid="mobile-bottom-nav"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: 56,
        background: 'var(--surface)',
        borderTop: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'stretch',
        zIndex: 20,
      }}
    >
      {items.map((item) => {
        const active =
          location.pathname === item.to ||
          (item.to !== '/' && location.pathname.startsWith(`${item.to}/`))
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-label={item.label}
            aria-current={active ? 'page' : undefined}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              fontSize: 10,
              fontWeight: active ? 600 : 400,
              color: active ? 'var(--primary-fg)' : 'var(--text-3)',
              textDecoration: 'none',
              minHeight: 44,
            }}
          >
            <Icon
              name={item.icon}
              size={20}
              fill={active}
              color={navIconColor(item.to)}
              aria-hidden
            />
            <span style={{ lineHeight: 1 }}>{item.label}</span>
          </Link>
        )
      })}
      <button
        type="button"
        onClick={onOpenDrawer}
        aria-label="More navigation options"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          fontSize: 10,
          color: 'var(--text-3)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          minHeight: 44,
        }}
      >
        <Icon name="more_horiz" size={20} aria-hidden />
        <span style={{ lineHeight: 1 }}>More</span>
      </button>
    </nav>
  )
}

function AppShellInner({ children }: Readonly<AppShellProps>): JSX.Element {
  const { user, isLoading } = useSession()
  const { isMobile, isTablet } = useViewport()
  const { collapsed, toggle, width: sidebarWidth, setWidth: setSidebarWidth } = useSidebarCollapse()
  const [drawerOpen, setDrawerOpen] = useState(false)
  // Read pathname unconditionally so hook order stays stable across the
  // early-return branches below — used to key the page-transition wrapper
  // around <main>'s children so each route mounts with a soft fade-up.
  const location = useLocation()
  const navigate = useNavigate()

  // Force-rotate guard: when the session user is flagged
  // `mustChangePassword`, every authenticated route bounces to the
  // dedicated interstitial. The interstitial itself renders outside
  // the AppShell, so we only redirect on every OTHER pathname.
  const needsPasswordRotation =
    user?.mustChangePassword === true &&
    location.pathname !== '/change-password-required'
  useEffect(() => {
    if (needsPasswordRotation) {
      void navigate({ to: '/change-password-required' })
    }
  }, [needsPasswordRotation, navigate])

  // Compute realRole BEFORE the conditional returns so hook order is stable
  // across renders. When user is unset we default to 'employee'; the early
  // return below still skips the authenticated chrome, so the value is only
  // read on the rendered branch.
  const parsedRole = user ? roleSchema.safeParse(user.role) : null
  const realRole = parsedRole?.success ? parsedRole.data : 'employee'
  // Full role set — falls back to [realRole] when the API hasn't sent roles yet.
  const realRoles: Role[] =
    user?.roles && user.roles.length > 0 ? user.roles : [realRole]
  const effectiveRole = useEffectiveRole(realRole)

  // ── "Viewing As" leak guard ───────────────────────────────────────────────
  // Impersonation is HR+ only and is persisted to sessionStorage, which
  // SURVIVES a logout → login within the same tab. Without this, a "Viewing As"
  // selection made by an HR user leaks into a later Manager/Employee session in
  // the same tab: the home then queries the impersonated employee's
  // balances/health-bar and 403s (those routes are self-or-HR+ only), leaving
  // the cards blank. Clear a stale selection when the real session can't
  // impersonate (role below HR) or the signed-in identity changed.
  const { impersonated, clear: clearImpersonation } = useEffectiveEmployeeState()
  const prevRealIdRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    const realId = user?.id ?? null
    const identityChanged =
      prevRealIdRef.current !== undefined && prevRealIdRef.current !== realId
    prevRealIdRef.current = realId
    if (impersonated == null) return
    if (!roleAtLeast(realRole, 'hr') || identityChanged) {
      clearImpersonation()
    }
  }, [impersonated, user?.id, realRole, clearImpersonation])
  // Sidebar visibility must follow the *impersonated* identity, not the real
  // user. Two impersonation modes feed it:
  //   - "Viewing as" (HR, read-only) → folded into effectiveRole already.
  //   - "Acting as"  (EA delegation) → carries the principal's role on the
  //     delegation (principalRole); effectiveRole does NOT reflect it.
  // visibleItems() gates minimumRole/excludeForRoles on the ROLE SET, so
  // passing the real user's roles hid the principal's sections (e.g. an EA
  // acting as a manager never saw "My team"). When either mode is active,
  // evaluate against the single impersonated role instead.
  const { navRole, navRoles } = resolveNavIdentity({
    realRole,
    realRoles,
    effectiveRole,
  })
  const [shortcutsOpen, setShortcutsOpen] = useGlobalKeymap(effectiveRole)
  // Warm pending-action counts for the sidebar badges. Hook always called
  // to maintain stable hook order; internally role-gates the actual fetches.
  const navCounts = useNavCounts(effectiveRole)

  // The ProfileMenu shows the user's team name (in place of their role). The
  // session payload only carries team_id, so resolve the name from /me. Shares
  // the ['me','profile'] cache with ProfilePage so this adds no extra fetch
  // once either surface has loaded. Called before the early returns to keep
  // hook order stable; gated on an authenticated user.
  const meQuery = useQuery({
    queryKey: ['me', 'profile'],
    queryFn: getMe,
    enabled: Boolean(user),
    staleTime: 5 * 60 * 1000,
  })
  const teamName = meQuery.data?.team?.name ?? null

  if (isLoading) {
    return <BootSplash />
  }

  if (!user) {
    // Routes that require auth handle redirecting to /login. As a defence
    // in depth, render the children directly so the LoginPage can show.
    return <>{children}</>
  }

  if (needsPasswordRotation) {
    // The effect above has fired a navigate to /change-password-required;
    // suppress the shell chrome in the meantime so the user never sees a
    // half-rendered authenticated surface that they're not supposed to
    // reach until the password is rotated.
    return <BootSplash />
  }

  const showViewingAs = flags.viewingAs

  const sidebarVariant = collapsed && !isMobile ? 'collapsed' : 'expanded'
  const handleSignOut = (): void => {
    void startLogout()
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        // Page canvas uses sunken paper per handover spec §02 / §05.
        // Cards inside each page are `--surface` for perceived lift.
        background: 'var(--surface-2)',
      }}
    >
      <TopBar
        userName={user.name}
        userEmail={user.email}
        role={realRole}
        roles={realRoles}
        teamName={teamName}
        onSignOut={handleSignOut}
        hasLocalPassword={user.hasLocalPassword}
        isMobile={isMobile}
        isTablet={isTablet}
        viewingAs={
          showViewingAs ? <ViewingAsBar realRole={realRole} /> : null
        }
        {...(isMobile ? { onOpenMobileDrawer: () => setDrawerOpen(true) } : {})}
      />
      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, minWidth: 0 }}>
        {isMobile ? null : (
          <Sidebar
            currentRole={navRole}
            currentRoles={navRoles}
            variant={sidebarVariant}
            onToggle={toggle}
            counts={navCounts}
            width={sidebarWidth}
            onResize={setSidebarWidth}
            topOffset={HEADER_HEIGHT}
          />
        )}
        <main
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 1280,
              // Mobile adds bottom padding for the bottom-nav bar (56px) plus
              // a small buffer so content doesn't sit flush at the nav edge.
              // The horizontal page gutter lives on `.page` /
              // HomeLobbyPreview's container so all page surfaces share one
              // responsive value.
              padding: isMobile
                ? '16px 16px 80px'
                : '12px',
            }}
          >
            <FadeIn triggerKey={location.pathname} rise={4} duration="slow">
              {children}
            </FadeIn>
          </div>
        </main>
      </div>
      {isMobile ? (
        <>
          <MobileDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            currentRole={navRole}
            currentRoles={navRoles}
            userName={user.name}
            userEmail={user.email}
            teamName={teamName}
            onSignOut={handleSignOut}
          />
          <MobileBottomNav
            currentRole={navRole}
            currentRoles={navRoles}
            onOpenDrawer={() => setDrawerOpen(true)}
          />
        </>
      ) : null}
      <ShortcutsHelpModal
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
        role={effectiveRole}
      />
      {flags.policyAssistant ? <PolicyAssistantWidget /> : null}
    </div>
  )
}
